import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useAdobeMediaSDK } from "../../analytics/useAdobeMediaSDK";
import { useSnowplow } from "../../analytics/useSnowplow";
import * as Constants from "../../constants";
import { ONGOING_STATES, PLAYER_DEFAULT_VALUES } from "../../constants";
import { useMediaContext } from "../../contexts/MediaContext";
import { State } from "../../enums";
import { audioPlaybackRates } from "../../helpers/playbackRates";
import { useMux } from "../../hooks/useMux/useMux";
import usePlayAudio from "../../hooks/usePlayAudio/usePlayAudio";
import useSetupAudio from "../../hooks/useSetupAudio/useSetupAudio";
import useVolume from "../../hooks/useVolume/useVolume";
import { usePlayerStore } from "../../store/usePlayerStore";
import { focusColor } from "../../styles/variables";
import {
  PlayerEvent,
  PlayerEventCallback,
  SetPlayerApiFn
} from "../../types/common";
import { AudioElement } from "../YleAudioPlayer/AudioElement";

import { AudioPlayerControls } from "./AudioPlayerControls";

const AudioContainer = styled.div`
  border-radius: 4px;
  position: relative;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${focusColor};
  }
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  height: 40px;
  width: 100%;
`;

const initPlayerApi = ({
  containerRef,
  currentMediaRef,
  externalEvents,
  getState,
  setDestroy,
  setPlayerApi
}) => {
  const notAvailableWarning = (name: string) =>
    console.warn(`${name}() is not avaiable for audio player`);

  setPlayerApi({
    closeFullscreen: () => notAvailableWarning("closeFullScreen"),
    defaultParameters: () => PLAYER_DEFAULT_VALUES,
    destroy: () => {
      setDestroy(true);
    },
    focus: () => {
      containerRef.current?.focus();
    },
    hideChatButton: () => notAvailableWarning("hideChatButton"),
    hideControls: () => notAvailableWarning("hideControls"),
    mute: () => getState().player.setMuted(true, true),
    muted: () => getState().isMuted,
    on: (eventName: PlayerEvent, callback: PlayerEventCallback) => {
      if (Constants.playerEventOptions.includes(eventName)) {
        externalEvents.addListener(eventName, callback);
      }
    },
    openFullscreen: () => notAvailableWarning("openFullscreen"),
    pause: () => getState().player.pause(),
    play: () => {
      const currentState = currentMediaRef.current?.state;
      if (ONGOING_STATES.includes(currentState)) {
        return getState().player.play({ throwError: true });
      }
      return Promise.reject(
        `Media ${currentMediaRef.current?.programId} has state "${currentState}". Playing media failed.`
      );
    },
    playbackRates: () => audioPlaybackRates,
    seek: getState().player.seekTo,
    setAspectRatio: () => notAvailableWarning("setAspectRatio"),
    setPlaybackRate: getState().player.setPlaybackRate,
    showChatButton: () => notAvailableWarning("showChatButton"),
    showControls: () => notAvailableWarning("showControls"),
    startLoad: () => notAvailableWarning("startLoad"),
    stopLoad: () => notAvailableWarning("stopLoad"),
    unmute: () => getState().player.setMuted(false, true),
    volume: (value: number) => {
      getState().player.setVolume(value, true);
      return value;
    }
  });
};

const onKeyDown = ({
  event,
  getState,
  isPaused,
  keyboardVolumeChange,
  player
}) => {
  switch (event.key) {
    case "ArrowUp":
      keyboardVolumeChange(true);
      event.preventDefault();
      break;
    case "ArrowDown":
      keyboardVolumeChange(false);
      event.preventDefault();
      break;
    case " ":
      if (isPaused) {
        player.play();
      } else {
        player.pause();
      }
      event.preventDefault();
      break;
    case "ArrowLeft":
    case "ArrowRight":
      event.preventDefault();
      break;
    case "m":
    case "M":
      player.setMuted(!getState().isMuted, true);
      event.preventDefault();
  }
};

const onKeyUp = ({ audio, event, isLive, player }) => {
  switch (event.key) {
    case "ArrowLeft":
      event.preventDefault();
      if (audio && !isLive) {
        player.seekTo(audio.currentTime - 10);
      }
      break;
    case "ArrowRight":
      event.preventDefault();
      if (audio && !isLive) {
        player.seekTo(audio.currentTime + 10);
      }
      break;
  }
};

const YleAudioPlayer = ({
  setPlayerApi,
  state
}: {
  setPlayerApi: SetPlayerApiFn;
  state: State;
}) => {
  const { getState, useStoreState } = usePlayerStore();
  const analyticsEvents = useStoreState.analyticsEvents();
  const snowplowEvents = useStoreState.snowplowEvents();
  const externalEvents = useStoreState.externalEvents();
  const isLive = useStoreState.isLive();
  const isPaused = useStoreState.isPaused();
  const isPlaybackStarted = useStoreState.isPlaybackStarted();
  const player = useStoreState.player();
  const programId = useStoreState.programId();

  const setIsDestroyed = useStoreState.setIsDestroyed();
  const setDurationInSeconds = useStoreState.setDurationInSeconds();

  const { manifestUrl, mediaUrl } = useMediaContext();

  const [audio, setAudio] = useState<HTMLAudioElement>();
  const [destroy, setDestroy] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const audioRef = useRef<HTMLAudioElement>();
  const containerRef = useRef<HTMLDivElement>(null);
  const currentMediaRef = useRef<{ programId?: string; state: State }>({
    programId,
    state
  });
  const playerPromiseResolvedRef = useRef(false);

  const getDuration = useCallback(() => {
    if (audio?.duration && audio?.duration !== Infinity) {
      setDurationInSeconds(audio.duration);
    }
  }, [audio, setDurationInSeconds]);

  useEffect(() => {
    currentMediaRef.current = { programId, state };
  }, [programId, state]);

  useEffect(() => {
    if (destroy) {
      setIsDestroyed(true);
      setAudio(undefined);
      analyticsEvents?.onFinish();
      snowplowEvents?.onDestroy();
    }
  }, [analyticsEvents, snowplowEvents, destroy, setIsDestroyed]);

  useEffect(() => {
    audio?.addEventListener("loadedmetadata", getDuration);
    return () => {
      audio?.removeEventListener("loadedmetadata", getDuration);
    };
  }, [audio, getDuration]);

  useSetupAudio({
    audio,
    destroy,
    manifestUrl,
    mediaUrl
  });
  usePlayAudio(audio);
  const { keyboardVolumeChange } = useVolume({
    audio,
    setShowVolumeSlider
  });

  useSnowplow();
  useAdobeMediaSDK({
    playerName: "YlePlayer __PLAYER_STATIC_BUILD_VERSION__"
  });

  useMux({ mediaElement: audio, type: "audio" });

  useEffect(() => {
    if (audioRef.current) {
      setAudio(audioRef.current);
    }
  }, [audioRef]);

  useEffect(() => {
    if (audio && externalEvents && !playerPromiseResolvedRef.current) {
      initPlayerApi({
        containerRef,
        currentMediaRef,
        externalEvents,
        getState,
        setDestroy,
        setPlayerApi
      });
      playerPromiseResolvedRef.current = true;
    }
  }, [audio, getState, setPlayerApi, state, player, externalEvents]);

  return destroy ? null : (
    <AudioContainer
      data-testid="yle-audio-player"
      onKeyDown={event =>
        onKeyDown({ event, getState, isPaused, keyboardVolumeChange, player })
      }
      onKeyUp={event => onKeyUp({ audio, event, isLive, player })}
      ref={containerRef}
      tabIndex={!isPlaybackStarted ? -1 : 0}>
      <Container>
        <AudioElement setAudio={setAudio} audioRef={audioRef} />
        <AudioPlayerControls
          audio={audio}
          containerRef={containerRef}
          setShowVolumeSlider={setShowVolumeSlider}
          showVolumeSlider={showVolumeSlider}
          state={state}
        />
      </Container>
    </AudioContainer>
  );
};

export default YleAudioPlayer;
